import { useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $usersStore } from '@lib/store';
import { ValidateUtils } from '@shared/utils';
import { UsersRequest, UserType } from '@shared/api';
import { SelectUserAccountsAndCards } from './selectUserAccountsAndCards';
import { BaseInput, Button, ErrorMessage, Modal, PhoneInput, Radio } from '@shared/widgets';
import { Constants } from '@shared/constants';

export interface AddNewUserFormData {
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  position: string;
  phone: { countryCode: string; phoneNumber: string };
  userType: UserType;
  accounts: number[];
  account?: number;
  cards: number[];
}
const DEFAULT_VALUES: AddNewUserFormData = {
  userName: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  position: '',
  phone: { countryCode: Constants.COUNTRY_CODE, phoneNumber: '' },
  userType: UserType.User,
  accounts: [],
  cards: [],
};

interface AddNewUserModalProps {
  show: boolean;
  onClose: () => void;
  params: UsersRequest;
}

export const AddNewUserModal = (props: AddNewUserModalProps) => {
  const form = useForm<AddNewUserFormData>({ defaultValues: DEFAULT_VALUES });
  const { control, formState, handleSubmit, watch, setValue, reset } = form;
  const userType = watch('userType');
  const { t } = useTranslation('userManagement');

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: AddNewUserFormData) => {
    await $usersStore.events.addUser({
      user: {
        userName: data.userName,
        firstName: data.firstName,
        lastName: data.lastName,
        emailAddress: data.emailAddress,
        position: data.position,
        phoneAreaCode: data.phone.countryCode,
        phoneNumber: data.phone.phoneNumber,
        userType: data.userType,
        accountId: data.accounts,
        cardId: data.cards,
      },
      params: props.params,
    });
    closeHandler();
  };

  const userTypeOptions = useMemo(
    () => [
      { value: UserType.Manager, text: t('modals.add.userTypes.manager') },
      { value: UserType.User, text: t('modals.add.userTypes.user') },
    ],
    [t],
  );
  return (
    <Modal isOpen={props.show} onClose={closeHandler} title={t('modals.add.title')}>
      <FormProvider {...form}>
        <form className='flex flex-col gap-5' noValidate onSubmit={handleSubmit(submitHandler)}>
          <Controller
            name='userName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              minLength: { value: 6, message: t('modals.add.minimumError') },
              maxLength: { value: 30, message: t('errors.form.maxCharacters', { ns: 'common', max: 30 }) },
              pattern: { value: ValidateUtils.noSpacesRegex, message: t('errors.form.noSpaces', { ns: 'common' }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} placeholder={t('placeholders.username', { ns: 'common' })} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='firstName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} placeholder={t('placeholders.firstName', { ns: 'common' })} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='lastName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} placeholder={t('placeholders.lastName', { ns: 'common' })} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='emailAddress'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} type='email' placeholder={t('placeholders.email', { ns: 'common' })} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='position'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              maxLength: { value: 64, message: t('errors.form.maxCharacters', { ns: 'common', max: 64 }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} placeholder={t('placeholders.positionRole', { ns: 'common' })} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='phone'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              validate: (value) => {
                if (!value?.countryCode) return t('errors.form.required', { ns: 'common' });
                if (!value?.phoneNumber) return t('errors.form.required', { ns: 'common' });
                if (!ValidateUtils.validatePhoneNumber(value.phoneNumber)) return t('errors.form.phone.invalidFormat', { ns: 'common' });
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <PhoneInput enableSearch required value={field.value} onChange={field.onChange} />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='userType'
            control={control}
            render={({ field }) => (
              <Radio
                orientation='horizontal'
                options={userTypeOptions}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  if (userType === UserType.User) {
                    setValue('account', undefined);
                    setValue('cards', []);
                  } else setValue('accounts', []);
                }}
              />
            )}
          />
          <SelectUserAccountsAndCards />
          <Button className='mt-7' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
            {t('buttons.saveChanges', { ns: 'common' })}
          </Button>
        </form>
      </FormProvider>
    </Modal>
  );
};
